import { addDays, formatLocalDate, isoWeekday, parseLocalDate } from "../../../domain/time";
import type { LocalDate } from "../../../domain/time";

/** Granularity of the times view (spec 11.1: day, week, month). */
export type PeriodView = "day" | "week" | "month";

/** An inclusive date range. */
export interface DateRange {
  readonly from: LocalDate;
  readonly to: LocalDate;
}

/**
 * Checks whether a value is a period view.
 *
 * @param value - The value.
 * @returns `true` for `day`, `week` or `month`.
 */
export function isPeriodView(value: unknown): value is PeriodView {
  const valid = value === "day" || value === "week" || value === "month";

  return valid;
}

/**
 * Returns the last day of a month.
 *
 * @param year - Year.
 * @param month - Month 1–12.
 * @returns Day of month.
 */
function daysInMonth(year: number, month: number): number {
  const days = new Date(Date.UTC(year, month, 0)).getUTCDate();

  return days;
}

/**
 * Returns the range of the period that contains a date (ISO weeks start on Monday).
 *
 * @param view - Granularity.
 * @param anchor - A date inside the period.
 * @returns The inclusive range.
 */
export function periodRange(view: PeriodView, anchor: LocalDate): DateRange {
  let range: DateRange;

  if (view === "day") {
    range = { from: anchor, to: anchor };
  } else if (view === "week") {
    const monday = addDays(anchor, 1 - isoWeekday(anchor));
    range = { from: monday, to: addDays(monday, 6) };
  } else {
    const { year, month } = parseLocalDate(anchor);
    range = {
      from: formatLocalDate(year, month, 1),
      to: formatLocalDate(year, month, daysInMonth(year, month)),
    };
  }

  return range;
}

/**
 * Moves the anchor date by whole periods.
 *
 * @param view - Granularity.
 * @param anchor - Current anchor.
 * @param steps - Number of periods (negative: back).
 * @returns The new anchor (first day of the target period for weeks and months).
 */
export function shiftPeriod(view: PeriodView, anchor: LocalDate, steps: number): LocalDate {
  let shifted: LocalDate;

  if (view === "day") {
    shifted = addDays(anchor, steps);
  } else if (view === "week") {
    shifted = addDays(periodRange("week", anchor).from, 7 * steps);
  } else {
    const { year, month } = parseLocalDate(anchor);
    const index = year * 12 + (month - 1) + steps;
    shifted = formatLocalDate(Math.floor(index / 12), (index % 12) + 1, 1);
  }

  return shifted;
}

/**
 * Lists all dates of a range.
 *
 * @param range - The range.
 * @returns Dates in order.
 */
export function datesOf(range: DateRange): LocalDate[] {
  const dates: LocalDate[] = [];

  for (let date = range.from; date <= range.to; date = addDays(date, 1)) {
    dates.push(date);
  }

  return dates;
}
